import { addDays, format, startOfDay } from "date-fns";
import type { PlaceOrderPayload, PlaceOrderResponse } from "../../apis/order";
import type { Product } from "../../apis/products";
import { useTranslation } from "react-i18next";
import { useApi, useNavigation } from "../../hooks";
import useForm from "../../hooks/form";
import * as React from "react";
import { useFieldArray } from "react-hook-form";
import type { FormField } from "../../components";
import { Constants, Tools } from "../../utils";
import { APIs } from "../../apis";
import { useAuthStore } from "../../stores";

type BookingFormData = {
  termsAccepted: boolean;
  serviceDate: string;
  serviceTime: string;
  order: PlaceOrderPayload;
};

const defaultOrderItem = {
  serviceDate: "",
  serviceTime: "",
  visitorFirstName: "",
  visitorLastName: "",
  visitorEmailAddress: "",
  visitorPhoneNumber: "+31",
  hasInsuranceProvider: 0,
  insuranceProviderName: "",
  insurancePolicyNumber: "",
  partnerProductId: "",
  partnerLocationId: "",
} satisfies PlaceOrderPayload["order_items"][number];

export function useViewModel() {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const product = navigation.data as Product;
  const prevPageData = navigation.routes.at(-1)?.data as
    | PlaceOrderResponse
    | undefined;
  const { user } = useAuthStore();
  const { fetch } = useApi();
  const slot =
    prevPageData?.order?.order_items?.[0]?.serviceDateTime ??
    product?.slots?.[0];
  const form = useForm<BookingFormData>({
    shouldUnregister: true,
    defaultValues: {
      termsAccepted: false,
      ...(!slot
        ? {}
        : {
            serviceDate: format(slot, "yyyy-MM-dd"),
            serviceTime: format(slot, "HH:mm"),
          }),
      order: {
        sendVisitorBookingConfirmationEmail: 1,
        bookerFirstName: user?.firstName,
        bookerLastName: user?.lastName,
        isGuestOrder: 0,
        billingPersonFirstName:
          user?.user_organization?.organization?.contactPersonFirstName ?? "",
        billingPersonLastName:
          user?.user_organization?.organization?.contactPersonLastName ?? "",
        billingPersonContactNumber:
          user?.user_organization?.organization?.phoneNumber ?? "+31",
        billingPersonEmail:
          user?.user_organization?.organization?.contactPersonEmail ?? "",
        order_items: [defaultOrderItem],
        ...(prevPageData?.order ?? {}),
      },
    },
  });
  const {
    fields: order_items,
    append,
    remove,
  } = useFieldArray({ control: form.control, name: "order.order_items" });

  const dateOptions = React.useMemo(() => {
    const today = startOfDay(new Date());
    const keys: string[] = [];
    for (let i = 0; i < 7; i++) {
      keys.push(format(addDays(today, i), "yyyy-MM-dd"));
    }
    return keys.map((dateKey) => ({
      value: dateKey,
      label: Tools.formatSmartDate(dateKey),
    }));
  }, []);

  const peopleOptions = React.useMemo(
    () => [
      { value: 0, label: `1 ${t("general.person")}` },
      { value: 1, label: `2 ${t("general.people")}` },
    ],
    [t],
  );

  const getMemberFields = React.useCallback(
    (index: number): (FormField | FormField[])[] => [
      [
        {
          name: `order.order_items.${index}.visitorFirstName`,
          label: t("booking.fields.firstName"),
          placeholder: t("booking.placeholders.firstName"),
          rules: Tools.bookingNameRules(t),
        },
        {
          name: `order.order_items.${index}.visitorLastName`,
          label: t("booking.fields.lastName"),
          placeholder: t("booking.placeholders.lastName"),
          rules: Tools.bookingNameRules(t),
        },
      ],
      [
        {
          name: `order.order_items.${index}.visitorEmailAddress`,
          label: t("booking.fields.email"),
          placeholder: t("booking.placeholders.email"),
          rules: Tools.bookingEmailRules(t),
        },
        {
          name: `order.order_items.${index}.visitorPhoneNumber`,
          label: t("booking.fields.mobileNumber"),
          placeholder: t("booking.placeholders.mobileNumber"),
          face: "phone" as const,
          rules: Tools.bookingPhoneRules(t),
        },
      ],
      [
        {
          name: `order.order_items.${index}.insuranceProviderName`,
          label: t("booking.fields.insuranceProvider"),
          placeholder: t("booking.placeholders.insuranceProvider"),
          rules: Tools.bookingInsuranceRules(t),
        },
        {
          name: `order.order_items.${index}.insurancePolicyNumber`,
          label: t("booking.fields.policyNumber"),
          placeholder: t("booking.placeholders.policyNumber"),
          rules: Tools.bookingPolicyRules(t),
        },
      ],
    ],
    [t],
  );

  const billingFields = React.useMemo<(FormField | FormField[])[]>(
    () => [
      [
        {
          name: "order.billingPersonFirstName",
          label: t("booking.fields.firstName"),
          placeholder: t("booking.placeholders.firstName"),
          rules: Tools.bookingNameRules(t),
        },
        {
          name: "order.billingPersonLastName",
          label: t("booking.fields.lastName"),
          placeholder: t("booking.placeholders.lastName"),
          rules: Tools.bookingNameRules(t),
        },
      ],
      [
        {
          name: "order.billingPersonEmail",
          label: t("booking.fields.email"),
          placeholder: t("booking.placeholders.email"),
          rules: Tools.bookingEmailRules(t),
        },
        {
          name: "order.billingPersonContactNumber",
          label: t("booking.fields.mobileNumber"),
          placeholder: t("booking.placeholders.mobileNumber"),
          face: "phone" as const,
          rules: Tools.bookingPhoneRules(t),
        },
      ],
    ],
    [t],
  );

  const bookerFields = React.useMemo<(FormField | FormField[])[]>(
    () => [
      [
        {
          name: "order.bookerFirstName",
          label: t("booking.fields.bookerFirstName"),
          placeholder: t("booking.placeholders.firstName"),
          rules: Tools.bookingNameRules(t),
        },
        {
          name: "order.bookerLastName",
          label: t("booking.fields.bookerLastName"),
          placeholder: t("booking.placeholders.lastName"),
          rules: Tools.bookingNameRules(t),
        },
      ],
    ],
    [t],
  );

  const onPeopleCountChange = React.useCallback(
    (v: unknown) => {
      if (v) append(defaultOrderItem);
      else remove(1);
    },
    [append, remove],
  );

  const onProceed = React.useCallback(
    async ({ order, serviceDate, serviceTime, ...extra }: BookingFormData) => {
      try {
        const payload = {
          ...order,
          telehealth: product.telehealth,
          partnerId: product.partnerId,
          countryCode: product.countryCode,
          order_items: order.order_items.map((_) => ({
            ..._,
            serviceDate,
            serviceTime,
            partnerProductId: product.id,
            partnerLocationId: product?.partner_location?.id ?? "",
          })),
        };
        const data = await fetch(async () => APIs.order.placeOrder(payload));
        if (data?.order?.orderStatus === "CONFIRMED") {
          navigation.navigate(
            Constants.urls.bookingSuccess({
              order_id: data.order.id,
              code: data.order.code,
            }),
            { replace: true },
          );
        } else {
          data.extra = extra;
          navigation.navigate(Constants.RoutePath.BookingPayment, { data });
        }
      } catch {}
    },
    [product, fetch, navigation],
  );

  const summary = React.useMemo(() => {
    const orderCount = order_items.length;
    const price = product?.totalAdvancePrice ?? 0;
    const discountedPrice = product?.totalAdvanceDiscountPrice ?? 0;
    const originalPrice = price * orderCount;
    const finalPrice = (discountedPrice || price) * orderCount;

    return {
      price,
      discountedPrice,
      orderCount,
      originalPrice,
      finalPrice,
      location: product?.partner_location ?? null,
      service: product?.partner_service?.service,
    };
  }, [order_items.length, product]);

  return {
    t,
    product,
    location: product?.partner_location ?? null,
    slots: product?.slots ?? [],
    dateOptions,
    form,
    order_items,
    peopleOptions,
    onPeopleCountChange,
    getMemberFields,
    billingFields,
    bookerFields,
    summary,
    onProceed,
  };
}

export type BookingViewModel = ReturnType<typeof useViewModel>;
